import { useUserData } from 'auth'
import BrandLogo from 'components/brandLogo'
import { GiftIcon, SettingsRoundedIcon, ShieldIcon } from 'components/icons'
import NotificationsDropdown from 'components/notificationsDropdown'
import { useOrgId } from 'helpers'
import { useMatch, useNavigate } from 'react-router'
import { SizableText, Text, View, XStack } from 'tamagui'

/** A 32×32 icon button for the top bar's right cluster. */
function IconButton({
    active,
    onPress,
    children,
    badge,
}: {
    active?: boolean
    onPress: () => void
    children: React.ReactNode
    badge?: number
}) {
    return (
        <View
            position="relative"
            width={32}
            height={32}
            borderRadius="$3"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            animation="quick"
            borderWidth={1}
            borderColor={active ? 'transparent' : '$borderColor'}
            backgroundColor={active ? '$accentSoft' : 'transparent'}
            hoverStyle={{ backgroundColor: active ? '$accentSoft' : '$backgroundHover' }}
            pressStyle={{ opacity: 0.85 }}
            onPress={onPress}
        >
            {children}
            {badge !== undefined && badge > 0 && (
                <View
                    position="absolute"
                    top={-5}
                    right={-5}
                    backgroundColor="$statusDanger"
                    borderRadius={999}
                    paddingHorizontal={badge > 9 ? 3 : 0}
                    minWidth={16}
                    height={16}
                    alignItems="center"
                    justifyContent="center"
                    borderWidth={2}
                    borderColor="$backgroundStrong"
                >
                    <Text
                        fontSize={10}
                        fontWeight="600"
                        color="white"
                    >
                        {badge > 9 ? '9+' : badge}
                    </Text>
                </View>
            )}
        </View>
    )
}

export default function TopBar() {
    const { user } = useUserData()
    const orgId = useOrgId()
    const navigate = useNavigate()

    const { currentPage } = useMatch('/:orgId/:currentPage/*')?.params ?? { currentPage: '' }

    const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()

    // Cog highlights for settings and the (soon-to-be-nested) user-management surfaces.
    const onSettings = ['settings', 'users', 'adduser', 'roles', 'conjunctionalagencies'].includes(
        currentPage ?? '',
    )

    return (
        <XStack
            alignItems="center"
            gap="$2"
            height={64}
            flexShrink={0}
            paddingHorizontal="$5"
            $sm={{ paddingHorizontal: '$3' }}
            borderBottomWidth={1}
            borderBottomColor="$borderColor"
            backgroundColor="$backgroundStrong"
            zIndex={100}
        >
            {/* Brand → home (Clients) */}
            <XStack
                alignItems="center"
                cursor="pointer"
                marginRight="$3"
                onPress={() => navigate(`/${orgId}/clients`)}
            >
                <BrandLogo />
            </XStack>

            <View flex={1} />

            {/* Right cluster: notifications · refer&earn · admin · settings · account */}
            <NotificationsDropdown />

            {user.isAffiliate && (
                <IconButton onPress={() => navigate('/affiliate')}>
                    <GiftIcon
                        size={18}
                        color="$colorSoft"
                    />
                </IconButton>
            )}

            {user.platformAdmin && (
                <IconButton onPress={() => navigate('/admin/affiliates')}>
                    <ShieldIcon
                        size={18}
                        color="$colorSoft"
                    />
                </IconButton>
            )}

            {user.permissions.includes('settings.view') && (
                <View data-tour="nav-settings">
                    <IconButton
                        active={onSettings}
                        onPress={() => navigate(`/${orgId}/settings`)}
                    >
                        <SettingsRoundedIcon
                            size={18}
                            color={onSettings ? '$accentText' : '$colorSoft'}
                        />
                    </IconButton>
                </View>
            )}

            {/* Account avatar — initials fallback, teal ring when active */}
            <View
                width={32}
                height={32}
                borderRadius={999}
                overflow="hidden"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                animation="quick"
                borderWidth={2}
                borderColor={currentPage === 'usersettings' ? '$primary' : '$borderColor'}
                backgroundColor="$backgroundRaised"
                hoverStyle={{ borderColor: '$primary' }}
                pressStyle={{ opacity: 0.85 }}
                onPress={() => navigate(`/${orgId}/usersettings`)}
            >
                {user.profileImage ? (
                    <img
                        src={user.profileImage}
                        alt="account"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <SizableText
                        size="$1"
                        fontWeight="600"
                        color="$colorSoft"
                    >
                        {initials}
                    </SizableText>
                )}
            </View>
        </XStack>
    )
}
